import { FunctionComponent, useEffect, useState } from 'react';
import { ordersAPI } from '../../../../services/redwolfSdkAPI';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import Pagination from '../../../common/components/Pagination';
import * as Sentry from '@sentry/react';
import { dateFormat, reactSelectStyles } from '../../../../utils/constants';
import Select from 'react-select';
import DropdownIndicator from '../../../common/components/DropdownIndicator';

import { useGoogleAnalytics } from '../../../common/hooks/useGoogleAnalytics';
import useTechnicalErrorMessage from '../../../common/hooks/useTechnicalErrorMessage';
import useRequestRedwolfService from '../../../common/hooks/useRequestRedwolfService';
import { getToken } from '../../../../utils/localStorage';
import { getProductImage } from '../../../../utils/commonFunctions';
import { setOrderDetails } from '../../../../store/orderSlice';
import { useDispatch } from 'react-redux';
import lodash from 'lodash';
import TableSkeleton from '../../../common/skeletons/TableSkeleton';

const Orders: FunctionComponent = () => {
	const [ordersList, setOrdersList]: any = useState([]);
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const [currentPageNumber, setCurrentPageNumber] = useState(1);
	const [isSkeletonLoading, setIsSkeletonLoading] = useState(false);
	const [showTechnicalErrorMessage] = useTechnicalErrorMessage();
	const [logGoogleAnalyticsEvent] = useGoogleAnalytics();
	const [requestRedwolfService] = useRequestRedwolfService();
	const token = getToken();
	const dispatch = useDispatch();

	const transaction = Sentry.startInactiveSpan({ name: 'Orders' });

	const navigate = useNavigate();

	const ordersDropdownOptions = [
		{
			label: 'Newest',
			value: 'newest'
		},
		{
			label: 'Oldest',
			value: 'oldest'
		}
	];

	const onDropdownKeyDown = (event: any) => {
		if (event.code === 'Enter') {
			setIsDropdownOpen(!isDropdownOpen);
		}
	};

	const onDropdownMenuClose = () => {
		setIsDropdownOpen(false);
	};

	const onDropdownMenuOpen = () => {
		setIsDropdownOpen(true);
	};

	useEffect(() => {
		setIsSkeletonLoading(true);

		requestRedwolfService(ordersAPI.getVizioOrderHistory(token))
			.then((orderResponse: any) => {
				setIsSkeletonLoading(false);

				if (
					orderResponse.constructor.$metadata$.simpleName === 'Success' &&
					orderResponse.data.orders.toArray().length
				) {
					setOrdersList(
						orderResponse.data.orders
							.toArray()
							.sort((a: any, b: any) =>
								new Date(a.billingDate) < new Date(b.billingDate) ? 1 : -1
							)
					);
				}

				transaction && transaction.end();
			})
			.catch((err: any) => {
				setIsSkeletonLoading(false);
				transaction && transaction.end();
				showTechnicalErrorMessage();
				Sentry.captureException(err);
			});

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleOrderDetails = (event: any, orderDetails: any) => {
		event.preventDefault();
		logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_VIEW_ORDER_DETAILS');
		dispatch(setOrderDetails(orderDetails));
		navigate(`/order-details?orderid=${orderDetails.id}`);
	};

	const handleSorting = (event: any) => {
		if (event.value === 'newest') {
			setOrdersList(lodash.orderBy(ordersList, ['orderDate'], ['desc']));
		} else {
			setOrdersList(lodash.orderBy(ordersList, ['orderDate'], ['asc']));
		}
		setIsDropdownOpen(false);
	};

	const handlePagination = (currentPage: any) => {
		setCurrentPageNumber(parseInt(currentPage));
	};

	const paginationPerPage = 5;
	const indexOfLastItem = currentPageNumber * paginationPerPage;
	const indexOfFirstItem = indexOfLastItem - paginationPerPage;

	const currentOrdersList = ordersList.slice(indexOfFirstItem, indexOfLastItem);

	return (
		<div className="sb-pr-component orderlist-table" id="order-listing">
			<div className="subs-head">
				<h2 id="orders-title" className="ac-title">
					Orders
				</h2>

				{currentOrdersList.length > 0 && (
					<div className="subs-dropdown">
						<div className="select-field">
							<Select
								defaultValue={ordersDropdownOptions[0]}
								isSearchable={false}
								styles={reactSelectStyles}
								onMenuOpen={onDropdownMenuOpen}
								onMenuClose={onDropdownMenuClose}
								menuIsOpen={isDropdownOpen}
								onKeyDown={onDropdownKeyDown}
								options={ordersDropdownOptions}
								onChange={handleSorting}
								aria-labelledby="orders-title"
								components={{ IndicatorSeparator: () => null, DropdownIndicator }}
							/>
						</div>
					</div>
				)}
			</div>

			{isSkeletonLoading ? (
				<TableSkeleton />
			) : (
				currentOrdersList.length === 0 && (
					<p className="empty-title">You have not placed any orders.</p>
				)
			)}

			{currentOrdersList.length > 0 && (
				<div className="subs-table">
					<div id="order-data-summary"></div>

					<table className="table subscrip-table order-table">
						<thead>
							<tr>
								<th scope="col">Order Number</th>
								<th scope="col">Date</th>
								<th scope="col">Total</th>
								<th scope="colgroup" colSpan={2}>
									Status
								</th>
							</tr>
						</thead>

						<tbody>
							{currentOrdersList.map((order: any) => {
								const itemList = order.lineItems.toArray();
								const imageList = itemList[0].images && itemList[0].images.toArray();

								return (
									<tr key={order.orderNumber}>
										<td className="app-td">
											<div className="icon-title">
												<div className="sapp-icon">
													<img src={getProductImage(imageList)} alt={order.orderNumber} />
												</div>
												<div className="sapp-title">
													<h3 id={`orderdetailnumber-${order.orderNumber}`}>{order.orderNumber}</h3>
													<span className="status mob">{order.userState}</span>
												</div>
											</div>
										</td>

										<td className="app-td desk">
											<span className="date">{format(new Date(order.orderDate), dateFormat)}</span>
										</td>

										<td className="app-td">
											<span className="total">&#36;{order.total}</span>
											<span className="date mob">
												{format(new Date(order.orderDate), dateFormat)}
											</span>
										</td>

										<td className="app-td desk">
											<span className="status">{order.userState}</span>
										</td>

										<td className="app-td">
											<span className="update-link">
												<a
													href="/"
													onClick={(event: any) => handleOrderDetails(event, order)}
													className="link view-order-details"
													aria-describedby={`orderdetailnumber-${order.orderNumber}`}>
													View Details
												</a>
											</span>
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>

					{ordersList.length > paginationPerPage && (
						<div className="table-navigation">
							<Pagination
								totalNumberOfPages={ordersList.length}
								currentPage={1}
								paginationPerPage={5}
								onPaginationChange={handlePagination}
							/>
						</div>
					)}
				</div>
			)}
		</div>
	);
};

export default Orders;
